import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, MenuItem, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import {
  AutoAwesomeOutlined, CheckCircleOutlineRounded, LinkRounded, LockOutlined, PlayArrowRounded,
  RocketLaunchOutlined, SecurityRounded, SyncRounded,
} from '@mui/icons-material';

const apiUrl = import.meta.env.VITE_API_URL ?? '';
const tokenKey = 'gestionnaire-trafic-session';

type ProductForm = {
  accountId: string; pixelId: string; pageId: string; instagramActorId: string;
  productName: string; productUrl: string; imageUrl: string; primaryText: string; headline: string; description: string;
  salePrice: string; productCost: string; shippingCost: string; dailyBudget: string; totalBudget: string;
  durationDays: string; country: string; ageMin: string; ageMax: string; targetCpa: string; minimumRoas: string;
  activateImmediately: boolean;
};

type MetaStatus = {
  configured: boolean;
  apiVersion: string;
  dryRun: boolean;
  connection: null | { status: string; lastSyncAt?: string };
};

type LaunchResponse = {
  result: {
    dryRun: boolean; status: string; campaignId: string; adSetId: string; creativeId: string; adId: string; steps: string[];
    sourceDailyBudget?: number; sourceTotalBudget?: number; sourceCurrency?: string;
    dailyBudget?: number; totalBudget?: number; accountCurrency?: string; exchangeRate?: number; exchangeRateUpdatedAt?: string;
  };
  plan: { economics: { marginBeforeAds: number; maximumAcquisitionCost: number; breakEvenRoas: number }; limits: { maximumScheduledSpend: number } };
};

type LaunchAssets = {
  pages: Array<{ id: string; name: string }>;
  pixels: Array<{ id: string; name: string; lastActivityAt?: string }>;
  instagramAccounts: Array<{ id: string; name: string; username?: string }>;
};

const initialForm: ProductForm = {
  accountId: '1349603796192935',
  pixelId: '882359077587643',
  pageId: '',
  instagramActorId: '',
  productName: '',
  productUrl: 'https://honeymoon.ma/',
  imageUrl: '',
  primaryText: '',
  headline: '',
  description: '',
  salePrice: '',
  productCost: '',
  shippingCost: '',
  dailyBudget: '',
  totalBudget: '',
  durationDays: '3',
  country: 'MA',
  ageMin: '18',
  ageMax: '65',
  targetCpa: '',
  minimumRoas: '2',
  activateImmediately: false,
};

async function requestJson<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  const payload = await response.json() as { message?: string; issues?: Array<{ message: string }> } & T;
  if (!response.ok) throw new Error(payload.message ?? payload.issues?.[0]?.message ?? 'La requête a échoué');
  return payload;
}

function numberValue(value: string): number { return Number(value.replace(',', '.')); }

function validateProductForm(form: ProductForm): string | null {
  const requiredText = [
    form.accountId, form.pixelId, form.pageId, form.productName, form.productUrl,
    form.imageUrl, form.primaryText, form.headline,
  ];
  if (requiredText.some((value) => !value.trim())) return 'Complétez tous les champs obligatoires avant le lancement.';
  if (form.productName.trim().length < 2) return 'Le nom du produit doit contenir au moins 2 caractères.';
  if (form.primaryText.trim().length < 10) return 'Le texte principal doit contenir au moins 10 caractères.';
  if (form.headline.trim().length < 2) return 'Le titre doit contenir au moins 2 caractères.';
  try { new URL(form.productUrl); new URL(form.imageUrl); } catch { return 'Vérifiez le lien du produit et l’adresse publique de l’image.'; }

  const salePrice = numberValue(form.salePrice);
  const productCost = numberValue(form.productCost);
  const shippingCost = numberValue(form.shippingCost);
  const dailyBudget = numberValue(form.dailyBudget);
  const totalBudget = numberValue(form.totalBudget);
  const durationDays = numberValue(form.durationDays);
  const targetCpa = numberValue(form.targetCpa);
  const minimumRoas = numberValue(form.minimumRoas);
  const ageMin = numberValue(form.ageMin);
  const ageMax = numberValue(form.ageMax);
  const amounts = [salePrice, productCost, shippingCost, dailyBudget, totalBudget, durationDays, targetCpa, minimumRoas, ageMin, ageMax];
  if (!amounts.every(Number.isFinite)) return 'Vérifiez les montants et les paramètres de ciblage saisis.';
  if (salePrice <= 0 || dailyBudget <= 0 || totalBudget <= 0 || durationDays <= 0 || targetCpa <= 0 || minimumRoas <= 0) return 'Le prix, les budgets, la durée, le CPA et le ROAS doivent être supérieurs à zéro.';
  if (productCost < 0 || shippingCost < 0) return 'Les coûts ne peuvent pas être négatifs.';

  const marginBeforeAds = salePrice - productCost - shippingCost;
  if (marginBeforeAds <= 0) return 'Le prix de vente doit couvrir le produit et la livraison.';
  if (targetCpa > marginBeforeAds) return 'Le CPA cible ne peut pas dépasser la marge avant publicité.';
  if (totalBudget < dailyBudget) return 'Le budget total doit être supérieur ou égal au budget quotidien.';
  if (ageMin < 18 || ageMax > 65 || ageMax < ageMin) return 'Vérifiez la tranche d’âge : de 18 à 65 ans.';
  return null;
}

export function ProductTestPage() {
  const [form, setForm] = useState<ProductForm>(() => {
    const saved = localStorage.getItem('test-produit-brouillon');
    return saved ? { ...initialForm, ...JSON.parse(saved) as ProductForm } : initialForm;
  });
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey) ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [metaStatus, setMetaStatus] = useState<MetaStatus | null>(null);
  const [assets, setAssets] = useState<LaunchAssets | null>(null);
  const [result, setResult] = useState<LaunchResponse | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const margin = useMemo(() => numberValue(form.salePrice || '0') - numberValue(form.productCost || '0') - numberValue(form.shippingCost || '0'), [form.salePrice, form.productCost, form.shippingCost]);
  const formValidationMessage = useMemo(() => validateProductForm(form), [form]);
  const update = <K extends keyof ProductForm>(key: K, value: ProductForm[K]) => {
    const next = { ...form, [key]: value };
    setForm(next);
    localStorage.setItem('test-produit-brouillon', JSON.stringify(next));
  };

  const payload = () => ({
    accountId: form.accountId.trim(), pixelId: form.pixelId.trim(), pageId: form.pageId.trim(),
    instagramActorId: form.instagramActorId.trim(), productName: form.productName.trim(), productUrl: form.productUrl.trim(),
    imageUrl: form.imageUrl.trim(), primaryText: form.primaryText.trim(), headline: form.headline.trim(), description: form.description.trim(),
    salePrice: numberValue(form.salePrice), productCost: numberValue(form.productCost), shippingCost: numberValue(form.shippingCost),
    dailyBudget: numberValue(form.dailyBudget), totalBudget: numberValue(form.totalBudget), durationDays: numberValue(form.durationDays),
    countries: [form.country], ageMin: numberValue(form.ageMin), ageMax: numberValue(form.ageMax),
    targetCpa: numberValue(form.targetCpa), minimumRoas: numberValue(form.minimumRoas), activateImmediately: form.activateImmediately,
    budgetCurrency: 'MAD' as const,
    idempotencyKey: crypto.randomUUID(),
  });

  const loadMetaStatus = useCallback(async (sessionToken = token) => {
    if (!sessionToken) return setMetaStatus(null);
    try {
      setMetaStatus(await requestJson<MetaStatus>('/api/ads/meta/status', {}, sessionToken));
    } catch {
      localStorage.removeItem(tokenKey);
      setToken('');
      setMetaStatus(null);
    }
  }, [token]);

  useEffect(() => { void loadMetaStatus(); }, [loadMetaStatus]);

  const login = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const session = await requestJson<{ token: string }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      localStorage.setItem(tokenKey, session.token); setToken(session.token); setPassword('');
      await loadMetaStatus(session.token); setMessage('Session ouverte. Vous pouvez maintenant connecter Meta.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Connexion impossible'); }
    finally { setBusy(false); }
  };

  const connectMeta = async () => {
    setBusy(true); setMessage('');
    try {
      const response = await requestJson<{ authorizationUrl: string }>('/api/ads/meta/authorize', {}, token);
      window.location.assign(response.authorizationUrl);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Connexion Meta impossible'); setBusy(false); }
  };

  const loadAssets = async () => {
    setBusy(true); setMessage('');
    try {
      const discovered = await requestJson<LaunchAssets>(`/api/ads/meta/launch-assets?accountId=${encodeURIComponent(form.accountId)}`, {}, token);
      setAssets(discovered);
      const nextForm = {
        ...form,
        pageId: discovered.pages.length === 1 ? discovered.pages[0]!.id : form.pageId,
        pixelId: discovered.pixels.length === 1 ? discovered.pixels[0]!.id : form.pixelId,
        instagramActorId: discovered.instagramAccounts.length === 1 ? discovered.instagramAccounts[0]!.id : form.instagramActorId,
      };
      setForm(nextForm);
      localStorage.setItem('test-produit-brouillon', JSON.stringify(nextForm));
      setMessage(`${discovered.pages.length} Page(s), ${discovered.pixels.length} pixel(s) et ${discovered.instagramAccounts.length} compte(s) Instagram trouvés.`);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Recherche des ressources Meta impossible'); }
    finally { setBusy(false); }
  };

  const localSimulation = (): LaunchResponse => {
    const data = payload();
    const requiredText = [data.productName, data.productUrl, data.imageUrl, data.primaryText, data.headline, data.pageId];
    if (requiredText.some((value) => !value)) throw new Error('Complétez les champs obligatoires avant la simulation.');
    if (![data.salePrice, data.productCost, data.shippingCost, data.dailyBudget, data.totalBudget, data.targetCpa, data.minimumRoas].every(Number.isFinite)) throw new Error('Vérifiez les montants saisis.');
    const marginBeforeAds = data.salePrice - data.productCost - data.shippingCost;
    if (marginBeforeAds <= 0) throw new Error('Le prix de vente doit couvrir le produit et la livraison.');
    if (data.targetCpa > marginBeforeAds) throw new Error('Le CPA cible ne peut pas dépasser la marge avant publicité.');
    if (data.totalBudget < data.dailyBudget) throw new Error('Le budget total doit être supérieur au budget quotidien.');
    const suffix = data.idempotencyKey.slice(-8);
    return {
      result: {
        dryRun: true, status: 'SIMULATED', campaignId: `simulation-campagne-${suffix}`,
        adSetId: `simulation-ensemble-${suffix}`, creativeId: `simulation-creatif-${suffix}`, adId: `simulation-annonce-${suffix}`,
        steps: ['Campagne de ventes préparée', 'Ensemble de publicités configuré pour les achats', 'Créatif produit préparé', 'Annonce préparée', 'Activation simulée sans dépense réelle'],
      },
      plan: {
        economics: { marginBeforeAds, maximumAcquisitionCost: marginBeforeAds, breakEvenRoas: data.salePrice / marginBeforeAds },
        limits: { maximumScheduledSpend: Math.min(data.totalBudget, data.dailyBudget * data.durationDays) },
      },
    };
  };

  const execute = async (live: boolean) => {
    const validationError = validateProductForm(form);
    if (validationError) {
      setResult(null);
      setMessage(validationError);
      return;
    }
    setBusy(true); setMessage(''); setResult(null);
    try {
      const response = live ? await requestJson<LaunchResponse>('/api/product-tests/launch', {
        method: 'POST', body: JSON.stringify(payload()),
      }, token) : localSimulation();
      setResult(response);
      setMessage(live ? 'Le test publicitaire a été transmis à Meta.' : 'Simulation terminée : aucune dépense réelle n’a été engagée.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Impossible de préparer le test'); }
    finally { setBusy(false); }
  };

  const liveReady = Boolean(token && metaStatus?.configured && metaStatus.connection?.status === 'ACTIVE' && !metaStatus.dryRun);

  return <Box className="page product-test-page">
    <Box className="page-heading"><Box><Typography variant="h4">Tester un produit</Typography><span>Préparer, simuler et lancer une campagne Meta pilotée automatiquement</span></Box><Chip icon={<SecurityRounded />} label="BUDGET PLAFONNÉ" className="active-chip" /></Box>

    {message && <Alert severity={result ? 'success' : 'info'} onClose={() => setMessage('')} sx={{ mb: 2 }}>{message}</Alert>}

    <Box className="product-test-layout">
      <Box className="product-form-column">
        <Box className="panel form-section">
          <Box className="section-title"><span>1</span><Box><Typography variant="h6">Produit et rentabilité</Typography><Typography>Les chiffres servent à calculer les limites automatiques.</Typography></Box></Box>
          <Box className="form-grid">
            <TextField label="Nom du produit" value={form.productName} onChange={(e) => update('productName', e.target.value)} required />
            <TextField label="Lien du produit" value={form.productUrl} onChange={(e) => update('productUrl', e.target.value)} required />
            <TextField label="Prix de vente" type="number" value={form.salePrice} onChange={(e) => update('salePrice', e.target.value)} required />
            <TextField label="Coût d’achat" type="number" value={form.productCost} onChange={(e) => update('productCost', e.target.value)} required />
            <TextField label="Coût de livraison" type="number" value={form.shippingCost} onChange={(e) => update('shippingCost', e.target.value)} required />
            <TextField label="Marge avant publicité" value={`${Math.max(0, margin).toFixed(2)} MAD`} disabled />
          </Box>
        </Box>

        <Box className="panel form-section">
          <Box className="section-title"><span>2</span><Box><Typography variant="h6">Compte et mesure Meta</Typography><Typography>Les identifiants du compte et du pixel ont été préremplis.</Typography></Box></Box>
          <Box className="form-grid">
            <TextField label="Identifiant du compte publicitaire" value={form.accountId} onChange={(e) => update('accountId', e.target.value)} required />
            {assets?.pixels.length ? <TextField select label="Pixel Meta" value={form.pixelId} onChange={(e) => update('pixelId', e.target.value)} required>{assets.pixels.map((pixel) => <MenuItem key={pixel.id} value={pixel.id}>{pixel.name} — {pixel.id}</MenuItem>)}</TextField> : <TextField label="Identifiant du pixel" value={form.pixelId} onChange={(e) => update('pixelId', e.target.value)} required />}
            {assets?.pages.length ? <TextField select label="Page Facebook" value={form.pageId} onChange={(e) => update('pageId', e.target.value)} required>{assets.pages.map((page) => <MenuItem key={page.id} value={page.id}>{page.name}</MenuItem>)}</TextField> : <TextField label="Identifiant de la Page Facebook" value={form.pageId} onChange={(e) => update('pageId', e.target.value)} required helperText="Elle pourra être détectée automatiquement après la connexion Meta" />}
            {assets?.instagramAccounts.length ? <TextField select label="Compte Instagram — facultatif" value={form.instagramActorId} onChange={(e) => update('instagramActorId', e.target.value)}><MenuItem value="">Aucun compte Instagram</MenuItem>{assets.instagramAccounts.map((account) => <MenuItem key={account.id} value={account.id}>@{account.username ?? account.name}</MenuItem>)}</TextField> : <TextField label="Identifiant Instagram — facultatif" value={form.instagramActorId} onChange={(e) => update('instagramActorId', e.target.value)} />}
          </Box>
        </Box>

        <Box className="panel form-section">
          <Box className="section-title"><span>3</span><Box><Typography variant="h6">Annonce</Typography><Typography>L’image doit être disponible publiquement pour que Meta puisse la récupérer.</Typography></Box></Box>
          <Box className="form-grid">
            <TextField className="wide-field" label="Adresse publique de l’image" value={form.imageUrl} onChange={(e) => update('imageUrl', e.target.value)} required InputProps={{ startAdornment: <LinkRounded sx={{ mr: 1, color: 'text.disabled' }} /> }} />
            <TextField className="wide-field" label="Texte principal" value={form.primaryText} onChange={(e) => update('primaryText', e.target.value)} multiline minRows={3} required />
            <TextField label="Titre" value={form.headline} onChange={(e) => update('headline', e.target.value)} required />
            <TextField label="Description — facultative" value={form.description} onChange={(e) => update('description', e.target.value)} />
          </Box>
        </Box>

        <Box className="panel form-section">
          <Box className="section-title"><span>4</span><Box><Typography variant="h6">Budget, durée et ciblage</Typography><Typography>L’agent ne pourra jamais dépasser le budget total indiqué.</Typography></Box></Box>
          <Box className="form-grid three-columns">
            <TextField label="Budget quotidien moyen (DH)" type="number" value={form.dailyBudget} onChange={(e) => update('dailyBudget', e.target.value)} required />
            <TextField label="Budget total maximal (DH)" type="number" value={form.totalBudget} onChange={(e) => update('totalBudget', e.target.value)} required />
            <TextField label="Durée en jours" type="number" value={form.durationDays} onChange={(e) => update('durationDays', e.target.value)} required />
            <TextField select label="Pays" value={form.country} onChange={(e) => update('country', e.target.value)}><MenuItem value="MA">Maroc</MenuItem><MenuItem value="FR">France</MenuItem><MenuItem value="CA">Canada</MenuItem><MenuItem value="AE">Émirats arabes unis</MenuItem></TextField>
            <TextField label="CPA cible" type="number" value={form.targetCpa} onChange={(e) => update('targetCpa', e.target.value)} required />
            <TextField label="ROAS minimal" type="number" value={form.minimumRoas} onChange={(e) => update('minimumRoas', e.target.value)} required />
            <TextField label="Âge minimal" type="number" value={form.ageMin} onChange={(e) => update('ageMin', e.target.value)} />
            <TextField label="Âge maximal" type="number" value={form.ageMax} onChange={(e) => update('ageMax', e.target.value)} />
          </Box>
          <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={form.activateImmediately} onChange={(e) => update('activateImmediately', e.target.checked)} />} label="Activer automatiquement la campagne dès que toute la structure est créée" />
        </Box>
      </Box>

      <Box className="product-side-column">
        <Box className="panel readiness-card">
          <Typography variant="h6">État de préparation</Typography>
          <Box className="readiness-row"><CheckCircleOutlineRounded /><span>Compte publicitaire renseigné</span></Box>
          <Box className="readiness-row"><CheckCircleOutlineRounded /><span>Pixel Meta renseigné</span></Box>
          <Box className={`readiness-row ${form.pageId ? '' : 'pending'}`}><CheckCircleOutlineRounded /><span>{form.pageId ? 'Page Facebook renseignée' : 'Page Facebook manquante'}</span></Box>
          <Divider />
          <Typography className="connection-label">CONNEXION À L’APPLICATION</Typography>
          {!token ? <Box component="form" onSubmit={login} className="login-box">
            <TextField size="small" label="Adresse e-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField size="small" label="Mot de passe" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" variant="outlined" startIcon={<LockOutlined />} disabled={busy}>Ouvrir la session</Button>
          </Box> : <Stack gap={1}>
            <Chip label="SESSION OUVERTE" color="success" size="small" />
            <Button variant="outlined" onClick={connectMeta} disabled={busy || metaStatus?.connection?.status === 'ACTIVE'}>{metaStatus?.connection?.status === 'ACTIVE' ? 'Meta est connecté' : 'Connecter Meta'}</Button>
            {metaStatus?.connection?.status === 'ACTIVE' && <Button variant="outlined" startIcon={<SyncRounded />} onClick={loadAssets} disabled={busy}>Détecter les ressources Meta</Button>}
          </Stack>}
          {metaStatus && <Alert severity={liveReady ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
            {!metaStatus.configured ? 'Les identifiants de l’application Meta ne sont pas encore configurés.' : metaStatus.dryRun ? 'Le serveur est encore verrouillé en mode simulation.' : metaStatus.connection?.status !== 'ACTIVE' ? 'Connectez votre compte Meta.' : 'Le lancement réel est disponible.'}
          </Alert>}
        </Box>

        <Box className="panel launch-card">
          <AutoAwesomeOutlined className="launch-icon" />
          <Typography variant="h6">Lancement autonome</Typography>
          <Typography>L’action initiale autorise le test. Ensuite, l’agent pilote les budgets et les arrêts sans demander de validation.</Typography>
          <Typography variant="caption">Les montants sont toujours saisis en dirhams. La devise Meta est détectée et convertie avant le lancement.</Typography>
          <Button fullWidth variant="outlined" startIcon={<PlayArrowRounded />} onClick={() => execute(false)} disabled={busy || Boolean(formValidationMessage)}>Simuler le lancement</Button>
          <Button fullWidth variant="contained" startIcon={busy ? <CircularProgress size={17} color="inherit" /> : <RocketLaunchOutlined />} onClick={() => execute(true)} disabled={busy || !liveReady || Boolean(formValidationMessage)}>Lancer sur Meta</Button>
          {!liveReady ? <span className="disabled-help">Connexion Meta active et mode réel requis</span> : formValidationMessage && <span className="disabled-help">{formValidationMessage}</span>}
        </Box>

        {result && <Box className="panel result-card">
          <Chip label={result.result.dryRun ? 'SIMULATION RÉUSSIE' : 'TEST LANCÉ'} color="success" size="small" />
          <Typography variant="h6">Plan validé</Typography>
          <Box className="result-metrics"><span>Marge avant publicité<b>{result.plan.economics.marginBeforeAds.toFixed(2)} MAD</b></span><span>ROAS d’équilibre<b>{result.plan.economics.breakEvenRoas.toFixed(2)}×</b></span><span>Dépense maximale planifiée<b>{result.plan.limits.maximumScheduledSpend.toFixed(2)} MAD</b></span></Box>
          {!result.result.dryRun && result.result.accountCurrency && <Alert severity="success" sx={{ mb: 1.5 }}>
            Plafond Meta vérifié : <b>{result.result.totalBudget?.toFixed(2)} {result.result.accountCurrency}</b>, converti depuis <b>{result.result.sourceTotalBudget?.toFixed(2)} {result.result.sourceCurrency}</b>.
          </Alert>}
          {result.result.steps.map((step) => <Box className="result-step" key={step}><CheckCircleOutlineRounded />{step}</Box>)}
          {!result.result.dryRun && result.result.exchangeRate && <Typography variant="caption" component="a" href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer">Taux fourni par Exchange Rate API</Typography>}
        </Box>}
      </Box>
    </Box>
  </Box>;
}
